import { CommonResponse, CommonResponseList } from 'src/model/common';
import {
  BillingDetail,
  CampaignDetail,
  CheckoutSession,
  CreateIntentTransfer,
  CustomerId,
  InvoiceDetail,
  ListInvoices,
  ListPlans,
  PlanDetail,
  UsageInformation,
  UserSidebars,
} from 'src/model/payment';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import {
  IBillingDetailPayload,
  ICheckoutSessionPayload,
  ICreateIntentTransferPayload,
  IGetListInvoicesPayload,
} from 'src/utils/typings/dto/payment';
import { getRequest, postRequest } from './http';

function updateCustomerInformation(dto: IBillingDetailPayload) {
  const url = URL_ENDPOINT.payment.checkout + 'update-customer';
  return postRequest(url, dto) as Promise<CommonResponse>;
}

function cancelSubscription(subscriptionId: string) {
  const url = URL_ENDPOINT.payment.checkout + 'cancel-subscription/' + subscriptionId;
  return getRequest(url, {}) as Promise<CommonResponse>;
}

function getCustomerId() {
  const url = URL_ENDPOINT.payment.checkout + 'get-customer-id';
  return getRequest(url, {}, new CustomerId()) as Promise<CustomerId>;
}

function getCheckoutSession(dto: ICheckoutSessionPayload) {
  const url = URL_ENDPOINT.payment.checkout + 'create-checkout-session';
  return postRequest(url, dto) as Promise<CheckoutSession>;
}

function createIntentBankTransfer(dto: ICreateIntentTransferPayload) {
  const url = URL_ENDPOINT.payment.transfer + 'register';
  return postRequest(url, dto) as Promise<CreateIntentTransfer>;
}

function getBillingDetail() {
  const url = URL_ENDPOINT.payment.checkout + 'get-billing-details';
  return getRequest(url, {}, new BillingDetail()) as Promise<BillingDetail>;
}

function getPlans(campaign_slug?: string) {
  const url = URL_ENDPOINT.payment.plan + 'prices';
  return getRequest(url, { campaign_slug: campaign_slug || '' }, new ListPlans()) as Promise<ListPlans>;
}

function getPlanDetail(id: number, campaign_slug?: string) {
  const url = URL_ENDPOINT.payment.plan + 'detail/' + id;
  return getRequest(url, { campaign_slug: campaign_slug || '' }, new PlanDetail()) as Promise<PlanDetail>;
}

function getInvoices(query: IGetListInvoicesPayload) {
  const url = URL_ENDPOINT.payment.checkout + 'get-history-invoice';
  return postRequest(url, query) as Promise<ListInvoices>;
}

function getInvoiceDetail(id: string) {
  const url = URL_ENDPOINT.payment.checkout + 'get-invoice';
  return getRequest(url, { invoice_id: id }, new InvoiceDetail()) as Promise<InvoiceDetail>;
}

function getInvoiceStripeBySessionId(sessionId: string) {
  const url = URL_ENDPOINT.payment.checkout + 'get-invoice-details-by-session-id/' + sessionId;
  return getRequest(url, {}, new CommonResponse()) as Promise<CommonResponse>;
}

function getUsage() {
  const url = URL_ENDPOINT.payment.checkout + 'get-usage';
  return getRequest(url, {}, new UsageInformation()) as Promise<UsageInformation>;
}

function checkEntityPaid({ entityId, type }: { entityId: string; type: string }) {
  const url = URL_ENDPOINT.payment.checkout + 'check-entity-id-type-created-30-days';
  return getRequest(url, { entity_id: entityId, type }, new CommonResponse()) as Promise<CommonResponse>;
}

function calculatePricePlan({ priceId }: { priceId: string }) {
  const url = URL_ENDPOINT.payment.checkout + 'calculate-price-before-change-plan';
  return postRequest(url, { price_id: priceId }) as Promise<CommonResponse>;
}

function changePlan({ priceId }: { priceId: string }) {
  const url = URL_ENDPOINT.payment.checkout + 'update-plan';
  return postRequest(url, { price_id: priceId }) as Promise<CommonResponse>;
}

function subscriptionFreeTrial({
  planId,
  affiliateCode,
  campaignSlug,
}: {
  planId: number;
  affiliateCode?: string;
  campaignSlug?: string;
}) {
  const url = URL_ENDPOINT.payment.plan + 'subcription-trial';
  return postRequest(url, {
    plan_id: planId,
    affiliate_code: affiliateCode,
    campaign_slug: campaignSlug,
  }) as Promise<CommonResponse>;
}

function getCreditUsageLog(body: Record<string, any>) {
  const url = URL_ENDPOINT.payment.checkout + 'get-log-points';
  return getRequest(url, body) as Promise<CommonResponseList>;
}

function getPlanFeatures(params?: Record<string, string | number>) {
  const url = URL_ENDPOINT.payment.plan + 'features';
  return getRequest(url, params);
}

function getUserCreditUsage(params: Record<string, string | number>) {
  const url = URL_ENDPOINT.payment.checkout + 'get-chart-points-usage';
  return getRequest(url, params);
}

function getUserSideBar() {
  const url = URL_ENDPOINT.payment.plan + 'user-sidebar';
  return getRequest(url, {}) as Promise<UserSidebars>;
}

function getCampaignProgram(params: Record<string, string | number>) {
  const url = URL_ENDPOINT.payment.campaign + 'publish';
  return getRequest(url, params, new CampaignDetail()) as Promise<CampaignDetail>;
}

const PaymentService = {
  updateCustomerInformation,
  cancelSubscription,
  getCustomerId,
  getCheckoutSession,
  createIntentBankTransfer,
  getBillingDetail,
  getPlans,
  getPlanDetail,
  getInvoices,
  getInvoiceDetail,
  getInvoiceStripeBySessionId,
  getUsage,
  checkEntityPaid,
  calculatePricePlan,
  changePlan,
  subscriptionFreeTrial,
  getCreditUsageLog,
  getUserCreditUsage,
  getPlanFeatures,
  getUserSideBar,
  getCampaignProgram,
};

export default PaymentService;
